/**
 * SOL → USD spot price for the UI. Mirrors the backend's price sources
 * (services/api/src/solana/price.ts: CoinGecko primary, Binance fallback) so the
 * frontend can show balances in USD without a round-trip to the API.
 *
 * A single module-level singleton holds the latest price and shares it with
 * every subscriber (header, panels, …) — they all read one cached value and one
 * refresh timer instead of each hammering the public price APIs. The price
 * refreshes every REFRESH_MS and the last good value is kept across transient
 * failures so the display never flickers back to "…".
 */
import { useEffect, useState } from "react";

/** Ordered USD-per-SOL sources; first one returning a sane (> 0) number wins. */
const SOURCES: Array<() => Promise<number | null>> = [
  // CoinGecko simple price — CORS-enabled, no key.
  async () => {
    const r = await fetch(
      "https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd",
      { signal: AbortSignal.timeout(5_000) },
    );
    if (!r.ok) return null;
    const j = (await r.json()) as { solana?: { usd?: unknown } };
    const p = Number(j?.solana?.usd);
    return Number.isFinite(p) && p > 0 ? p : null;
  },
  // Binance spot ticker — fallback (geo-blocked in some regions, hence second).
  async () => {
    const r = await fetch(
      "https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT",
      { signal: AbortSignal.timeout(5_000) },
    );
    if (!r.ok) return null;
    const j = (await r.json()) as { price?: unknown };
    const p = Number(j?.price);
    return Number.isFinite(p) && p > 0 ? p : null;
  },
];

const REFRESH_MS = 60_000;

let cachedPrice: number | null = null;
let lastFetch = 0;
let inflight: Promise<number | null> | null = null;
const subscribers = new Set<(p: number | null) => void>();

async function fetchSolUsd(): Promise<number | null> {
  for (const src of SOURCES) {
    try {
      const p = await src();
      if (p != null) return p;
    } catch {
      /* try next source */
    }
  }
  return null;
}

/** Refresh the shared price at most once per REFRESH_MS (deduped across callers). */
function refresh(): Promise<number | null> {
  const now = Date.now();
  if (cachedPrice != null && now - lastFetch < REFRESH_MS) return Promise.resolve(cachedPrice);
  if (inflight) return inflight;
  inflight = fetchSolUsd()
    .then((p) => {
      if (p != null) {
        cachedPrice = p;
        lastFetch = Date.now();
        subscribers.forEach((fn) => fn(cachedPrice));
      }
      return cachedPrice;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Live SOL/USD price. `price` is null until the first successful fetch, after
 * which it stays populated (last good value) even if a later refresh fails.
 */
export function useSolUsdPrice(): { price: number | null; loading: boolean } {
  const [price, setPrice] = useState<number | null>(cachedPrice);

  useEffect(() => {
    subscribers.add(setPrice);
    void refresh();
    const t = setInterval(() => void refresh(), REFRESH_MS);
    return () => {
      subscribers.delete(setPrice);
      clearInterval(t);
    };
  }, []);

  return { price, loading: price == null };
}

/** Format a USD number for display, e.g. 1234.5 → "$1,234.50". */
export function formatUsd(usd: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(usd);
}
